// Top-level Billing Settings page. Mounted at /billing/settings.
//
// Reads/writes go through ./api.ts. (An earlier draft mirrored a
// `CCMSWeb.BillingSettingsLive.Index` LiveView in ws-common/Nova; that
// was rejected — moxit owns this surface. See PAYM-5075:
// https://linear.app/wonderschool/issue/PAYM-5075/provider-late-payment-policy-setup)
//
// Two tabs. Late Payment Fee carries this shell's dirty flag and Save
// button. Payments & Payouts is the former Finances page: it owns its own
// state and writes, so this shell just mounts it.
//
// The Email Templates tab was removed deliberately — providers set whether
// reminders send and on which days, not what they say. Editable bodies let a
// provider delete [payment_link] and mail families a demand with no way to
// pay, and asking a childcare director to hand-edit six templates with token
// substitution was the wrong ask. The copy is code-owned in defaults.ts.
//
// Tab state is held in the URL query (?tab=payments) so a refresh keeps you
// where you were.

import { StaffTypeEnum } from '@wonderschool/common-base-types';
import { InformationalBanner, Tab, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../Components/Shared/PageHeader';
import { useOrganization } from '../../hooks/useOrganizations';
import { useUser } from '../../hooks/useUser';
import { logError } from '../../rollbar';
import { useSegmentPage } from '../../segment';
import Finances from '../Finances';
import { fetchBillingSettings, saveLateFeePolicy } from './api';
import { DEFAULT_BILLING_SETTINGS } from './defaults';
import LateFeePolicyTab from './tabs/LateFeePolicyTab';
import type { BillingSettingsData, BillingSettingsTab, LateFeePolicy } from './types';

// Labels are i18n keys. "Late Payment Fee", never "Late Fee" — providers read
// the latter as the late PICKUP fee they already set in the Parent Handbook,
// and the Spanish reads the same way. PAYM-5075 4.7.
const TABS: { key: BillingSettingsTab; labelKey: string }[] = [
  { key: 'late_fee_policy', labelKey: 'billingSettings.tab.lateFees' },
  // Payments & payouts is the former /billing/settings page (Finances.jsx),
  // folded in here because this page took over its route. It owns its own
  // state and saves, so it gets no dirty flag or Save button from this shell.
  { key: 'payments_payouts', labelKey: 'billingSettings.tab.paymentsPayouts' },
];

// After this many consecutive failed loads, the error banner stops telling
// the provider to try again and points at reload / contact support instead.
const FAILURES_BEFORE_GIVING_UP = 4;

function parseTab(raw: string | null): BillingSettingsTab {
  switch (raw) {
    case 'payments_payouts':
    case 'payments':
      return 'payments_payouts';
    // No case for the retired 'email_templates' value (removed tab, see
    // header comment) — it falls through to the default on purpose and the
    // URL gets silently rewritten. Do not re-add it.
    default:
      return 'late_fee_policy';
  }
}

export default function BillingSettings() {
  const { t } = useTranslation();
  const organization = useOrganization();
  // firestore.rules allows write on billingSettings for organization admins
  // only, while the route admits any can_list_billing user. Without this the
  // form is fully editable for location admins and every save comes back as
  // permission-denied.
  // Mirrors validateOrganizationAdminForOrg in functions/src/helpers/validators.js,
  // the backend's blessed per-org admin predicate — including its `enabled !== false`
  // clause, so a disabled admin does not get an editable form whose saves are denied.
  // The snapshot marker must match too, or an org switch could briefly reuse
  // the previous organization's roles before the new listener delivers.
  // `!== false` rather than `=== true`: a doc omitting the field is not disabled.
  const { staffType, enabled, organizationId: userOrganizationId } = useUser();
  const rolesUnavailable = !Array.isArray(staffType);
  const canEdit =
    userOrganizationId === organization?.id &&
    !rolesUnavailable &&
    staffType.includes(StaffTypeEnum.organizationAdmin) &&
    enabled !== false;
  useSegmentPage('billing_settings');

  // -- Tab state, kept in the URL so refresh preserves it -----------------
  const [activeTab, setActiveTab] = React.useState<BillingSettingsTab>(() => {
    if (typeof window === 'undefined') return 'late_fee_policy';
    return parseTab(new URLSearchParams(window.location.search).get('tab'));
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (parseTab(params.get('tab')) === activeTab) return;
    params.set('tab', activeTab);
    const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState({}, '', next);
  }, [activeTab]);

  // -- Server-backed state ------------------------------------------------
  const [policy, setPolicy] = React.useState<LateFeePolicy>(DEFAULT_BILLING_SETTINGS.lateFeePolicy);

  // -- Dirty flags, per tab. Reset to false after a successful save. ------
  const [policyDirty, setPolicyDirty] = React.useState(false);

  // -- Per-tab saving spinners --------------------------------------------
  const [savingPolicy, setSavingPolicy] = React.useState(false);

  // Latest values, readable from an async save handler. Used to tell whether
  // the provider edited anything while the save was in flight — clearing the
  // dirty flag then would report those edits as saved without writing them.
  const policyRef = React.useRef(policy);
  policyRef.current = policy;

  // -- Banner for save success/failure feedback ---------------------------
  const [banner, setBanner] = React.useState<{
    tone: 'success' | 'error';
    text: string;
  } | null>(null);

  // Only a SUCCESS banner self-dismisses. An error must persist: this policy
  // decides whether families get charged automatically, and a provider who
  // misses a 4.5s window has no other signal that their change was not stored.
  // It clears on the next save attempt or an organization change.
  React.useEffect(() => {
    if (banner?.tone !== 'success') return;
    const t = setTimeout(() => setBanner(null), 4500);
    return () => clearTimeout(t);
  }, [banner]);

  // -- Initial load -------------------------------------------------------
  // The form stays disabled until the fetch settles. Editing against the
  // seeded defaults first would either lose the edit (the resolving fetch
  // overwrites it) or, if we kept the edit, save platform defaults over the
  // org's stored values for every field they did not touch.
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  // Consecutive failed loads for the current organization. Drives the error
  // copy: a near-instant failed retry otherwise repaints a pixel-identical
  // banner, which reads as a dead button. The count is a persistent record
  // of each attempt — unlike a brief spinner, it cannot flash by unseen —
  // and past FAILURES_BEFORE_GIVING_UP the copy stops suggesting another
  // click. Reset to 0 on any successful load and on org change.
  const [failedAttempts, setFailedAttempts] = React.useState(0);

  // Cancellation: every load (mount, org change, retry) bumps this counter
  // and captures its value; a resolving fetch whose captured value no longer
  // matches is stale and must not touch state. This is the old effect-local
  // `cancelled` flag, hoisted to a ref so the retry button can share the
  // loader — the effect cleanup bumps the counter to cancel in-flight
  // fetches on org change and unmount, exactly as before.
  const loadSeqRef = React.useRef(0);
  const saveSeqRef = React.useRef(0);

  const loadPolicy = React.useCallback((organizationId: string) => {
    // The effect cleanup increments this same ref to invalidate this load.
    const seq = ++loadSeqRef.current;
    setIsLoading(true);

    fetchBillingSettings(organizationId)
      .then((data: BillingSettingsData) => {
        if (seq !== loadSeqRef.current) return;
        setPolicy(data.lateFeePolicy);
        setLoadFailed(false);
        setFailedAttempts(0);
      })
      .catch(() => {
        if (seq !== loadSeqRef.current) return;
        // A rejection here is a genuine read failure (permission-denied,
        // offline, etc.) — a missing doc resolves instead, see api.ts.
        // Show the banner rather than silently rendering platform defaults
        // as though they were the org's saved policy.
        // getDocument already logged the failure with its document path.
        setLoadFailed(true);
        setFailedAttempts((n) => n + 1);
      })
      .finally(() => {
        if (seq === loadSeqRef.current) setIsLoading(false);
      });
  }, []);

  React.useEffect(() => {
    if (!organization?.id) return;

    // Switching organizations must not leave the previous org's edits (or
    // their dirty flags) armed — an enabled Save would write org A's policy
    // to org B before the fetch resolves.
    setPolicyDirty(false);
    setSavingPolicy(false);
    setBanner(null);
    setLoadFailed(false);
    // A fresh org must not inherit the previous org's failure history.
    setFailedAttempts(0);
    loadPolicy(organization.id);

    return () => {
      // Pairs with loadPolicy's increment so an old org's load stays stale.
      loadSeqRef.current += 1;
      saveSeqRef.current += 1;
    };
  }, [organization?.id, loadPolicy]);

  // Retry keeps loadFailed set while in flight so the banner (and its
  // disabled form) stays put; success clears it in the loader. The isLoading
  // guard stops double-firing from repeated clicks.
  const onRetryLoad = () => {
    if (!organization?.id || isLoading) return;
    loadPolicy(organization.id);
  };

  // -- Patch handlers ------------------------------------------------------
  const updatePolicy = (patch: Partial<LateFeePolicy>) => {
    setPolicy((prev) => ({ ...prev, ...patch }));
    setPolicyDirty(true);
  };

  // -- Save handlers -------------------------------------------------------
  const onSavePolicy = async () => {
    if (!organization?.id) {
      logError('[BillingSettings] saveLateFeePolicy skipped because the organization is unavailable');
      setBanner({ tone: 'error', text: t('billingSettings.lateFees.saveError') });
      return;
    }
    const seq = ++saveSeqRef.current;
    setSavingPolicy(true);
    const submitted = policy;
    try {
      const saved = await saveLateFeePolicy(organization.id, submitted);
      if (seq !== saveSeqRef.current) return;
      // Only clear dirty if nothing changed while the write was in flight.
      if (policyRef.current === submitted) {
        setPolicy(saved);
        setPolicyDirty(false);
      }
      setBanner({ tone: 'success', text: t('billingSettings.lateFees.saveSuccess') });
    } catch (err) {
      if (seq !== saveSeqRef.current) return;
      logError('[BillingSettings] saveLateFeePolicy failed', err);
      setBanner({ tone: 'error', text: t('billingSettings.lateFees.saveError') });
    } finally {
      if (seq === saveSeqRef.current) setSavingPolicy(false);
    }
  };

  return (
    <>
      <PageHeader pageName="billingSettings.pageTitle" classes="billing" />

      {/* 1024px cap, left-aligned against the side nav, never centered — the
          standard content width per Caitlin's CCMS content-area spec. Extra
          room becomes white space on the right rather than a wider column. */}
      <div className="max-w-5xl space-y-4 px-6 pb-32 pt-6" data-testid="billing-settings-page">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{t('billingSettings.pageTitle')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('billingSettings.pageDescription')}</p>
          </div>
        </div>

        {/* Disabled-state precedence: a failed policy read comes first, then
            unavailable roles, then a confirmed read-only role. This keeps
            the page to one explanation banner at a time. */}
        {!canEdit && !loadFailed && (
          <InformationalBanner
            type="info"
            label={t(rolesUnavailable ? 'billingSettings.accessUnavailableNotice' : 'billingSettings.readOnlyNotice')}
            size={WidgetSizeEnum.SMALL}
            extraClasses="text-sm"
          />
        )}

        {banner && (
          // role="status" lives on the wrapper: InformationalBanner renders no
          // live region of its own, so without it a save result is silent to
          // screen readers.
          <div role="status" aria-live="polite">
            <InformationalBanner
              type={banner.tone}
              label={banner.text}
              size={WidgetSizeEnum.SMALL}
              extraClasses="text-sm"
            />
          </div>
        )}

        {loadFailed && (
          <div role="status" aria-live="polite">
            <InformationalBanner
              type="error"
              // Copy escalates with consecutive failures so a failed retry
              // never repaints the identical banner: first failure asks to
              // try again; a failed retry says so and carries the attempt
              // number; past the give-up threshold it stops suggesting
              // clicks and points at reload/support.
              label={
                failedAttempts >= FAILURES_BEFORE_GIVING_UP
                  ? t('billingSettings.loadErrorPersistent', { attempt: failedAttempts })
                  : failedAttempts > 1
                    ? t('billingSettings.loadErrorRetry', { attempt: failedAttempts })
                    : t('billingSettings.loadError')
              }
              size={WidgetSizeEnum.SMALL}
              extraClasses="text-sm"
              // Renders as a button inside the banner. Label swaps to
              // "Retrying…" while the refetch is in flight; onRetryLoad's
              // isLoading guard makes extra clicks no-ops.
              linkLabel={t(isLoading ? 'billingSettings.retrying' : 'billingSettings.retry')}
              onClickLink={onRetryLoad}
            />
          </div>
        )}

        {/* Tab nav. TabGroup is unconditionally uncontrolled (it forwards no
            selectedIndex/onChange to headless-ui), and this page keeps the
            active tab in ?tab=, so the bare Tab atoms go in our own row. */}
        <div className="flex flex-wrap gap-3 pb-4">
          {TABS.map((tab) => (
            <Tab
              key={tab.key}
              label={t(tab.labelKey)}
              selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
            />
          ))}
        </div>

        {/* Tab panels */}
        {activeTab === 'late_fee_policy' && (
          <LateFeePolicyTab
            policy={policy}
            isDirty={policyDirty}
            isSaving={savingPolicy}
            canEdit={canEdit && !isLoading && !loadFailed}
            onChange={updatePolicy}
            onSave={onSavePolicy}
          />
        )}

        {/* Payments & payouts owns its own reads, writes and permission checks,
            so it takes none of this shell's state. It is not gated on canEdit:
            its settings live on the organization doc, not billingSettings, and
            have their own rules. */}
        {activeTab === 'payments_payouts' && <Finances />}
      </div>
    </>
  );
}
